import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Button } from '../Button';
import { PanelRightOpenIcon } from '../icons';

// Panneau d'artefacts (UI-03) : un seul artefact affiché à la fois.
//
// Artefact : {id, type, title, subtitle?, messageId, payload}
//   - id : stable par message et par élément (ex. « assistant-…:code:412 ») ; rouvrir
//     le même id ne recharge rien, un autre id remplace le contenu avec transition
//   - type : clé du registre des visionneuses (registry.jsx)
//   - payload : données propres au type (code, lignes du tableau, lien du fichier…)
//
// Deux contextes : les actions (stables) et l'état. Les messages n'écoutent que les
// actions ; seuls les boutons « Ouvrir » et le panneau suivent l'état.

const ActionsContext = createContext({ open: () => {}, close: () => {}, setFullscreen: () => {}, update: () => {} });
const StateContext = createContext({ artifact: null, fullscreen: false, trigger: null });

export function ArtifactProvider({ children }) {
  const [artifact, setArtifact] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  // Élément qui a ouvert le panneau : le focus y revient à la fermeture
  const triggerRef = useRef(null);

  const open = useCallback((next, trigger = null) => {
    if (!next?.id || !next?.type) return;
    triggerRef.current = trigger ?? (typeof document !== 'undefined' ? document.activeElement : null);
    setArtifact((current) => (current?.id === next.id ? { ...current, ...next } : next));
  }, []);

  const close = useCallback(() => {
    setArtifact(null);
    setFullscreen(false);
  }, []);

  // Mise à jour de l'artefact affiché (même id), sans toucher au déclencheur qui reprendra
  // le focus à la fermeture : type de visuel changé dans le panneau, par exemple
  const update = useCallback((id, patch) => {
    setArtifact((current) => (current?.id === id ? { ...current, ...patch } : current));
  }, []);

  const actions = useMemo(() => ({ open, close, setFullscreen, update }), [open, close, update]);
  const state = useMemo(() => ({ artifact, fullscreen, triggerRef }), [artifact, fullscreen]);

  return (
    <ActionsContext.Provider value={actions}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </ActionsContext.Provider>
  );
}

export const useArtifactActions = () => useContext(ActionsContext);
export const useArtifactState = () => useContext(StateContext);

export const ARTIFACT_PANEL_ID = 'artifact-panel';

/**
 * Bouton « Ouvrir dans le panneau » (composant Button, ghost ; affiché : aria-expanded).
 * `build` construit l'artefact au clic (lecture des cellules d'un tableau, par exemple).
 */
export function OpenArtifactButton({ artifactId, build, label = 'Ouvrir dans le panneau', showLabel = false, className = '', size = 14 }) {
  const { open } = useArtifactActions();
  const { artifact } = useArtifactState();
  const isOpen = artifact?.id === artifactId;

  return (
    <Button
      variant="ghost"
      size="sm"
      icon={PanelRightOpenIcon}
      iconSize={size}
      iconOnly={!showLabel}
      className={`artifact-open-button ${className}`.trim()}
      aria-label={showLabel ? undefined : label}
      aria-expanded={isOpen}
      aria-controls={isOpen ? ARTIFACT_PANEL_ID : undefined}
      title={isOpen ? 'Affiché dans le panneau' : label}
      onClick={(event) => {
        event.stopPropagation();
        const next = build();
        if (next) open(next, event.currentTarget);
      }}
    >
      {showLabel && <span>{isOpen ? 'Affiché' : 'Ouvrir'}</span>}
    </Button>
  );
}
